"use client";

import Link from "next/link";

import { useLanguage } from "../i18n/LanguageProvider";

/* ============================================================
   GOOGLE RATING

   Menampilkan penilaian AGREGAT, bukan kutipan ulasan.

   KENAPA BEGINI, BUKAN KARTU TESTIMONI:

   Permenkes No. 1787/MENKES/PER/XII/2010 pasal 5 melarang iklan dan
   publikasi pelayanan kesehatan memuat testimoni. Menampilkan kisah
   pasien di beranda rumah sakit berizin menempatkan RS pada risiko
   teguran Dinas Kesehatan - berbeda dari klinik kecantikan atau toko
   biasa yang tidak tunduk pada aturan itu.

   Angka rata-rata tidak sama dengan testimoni. Ia fakta yang bisa
   diverifikasi sendiri oleh pengunjung lewat tautan di bawahnya, tidak
   menjanjikan kesembuhan, dan tidak menyaring siapa pun. Justru lebih
   kuat: pengunjung bisa langsung mengecek, dan itu yang membuat sebuah
   angka dipercaya.

   Status regulasi sebaiknya dikonfirmasi ke bagian legal atau Dinkes
   setempat - lanskapnya sempat berubah dengan UU Kesehatan 17/2023.

   ============================================================
   CARA MEMPERBARUI ANGKANYA

   Buka halaman Google Business RS JMC, salin nilai rata-rata dan
   jumlah ulasannya persis seperti tertera di sana.

   JANGAN MENGARANG ANGKANYA, dan jangan membulatkan ke atas. Angka di
   halaman ini bisa dibandingkan langsung dengan halaman Google lewat
   tautan yang ada tepat di bawahnya - selisih sekecil apa pun akan
   terlihat, dan begitu terlihat, seluruh halaman ini kehilangan
   gunanya.

   Nilai dan jumlah selalu diperbarui BERSAMAAN. Membetulkan salah
   satunya saja meninggalkan yang lain tetap keliru, dan keduanya
   tampil di baris yang sama.

   Ikut perbarui `rating.diperiksa` di berkas kamus bahasa - teks
   bulan/tahun itu tidak berubah sendiri saat angka di sini diganti,
   dan tanggal pemeriksaan yang tertinggal justru menyesatkan.

   Ulasan terus bertambah, jadi angkanya akan usang. Perbarui berkala -
   tiap kuartal sudah memadai. Kalau nanti ingin otomatis, sumber yang
   benar adalah Google Places API, bukan menyalin dari halaman Google
   dengan skrip.
   ============================================================ */

const RATING = {
  /* Disalin dari halaman Google Business RS JMC, Agustus 2026. */
  nilai: 4.1,
  jumlah: 1175,

  /* Tautan yang kamu kirim. Buka sekali dan pastikan ia mendarat di
     halaman ulasan RS JMC, bukan di daftar tempat lain. */
  url: "https://share.google/f5PBhb0FH7arvhIjs",

  /* Tanggal pindah ke kamus sebagai rating.diperiksa, karena nama
     bulannya berbeda di tiap bahasa. Tetap ditulis manual, bukan dari
     new Date() - nilai yang berbeda antara render peladen dan klien
     memicu hydration error. */
};

function Bintang({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={`h-5 w-5 shrink-0 sm:h-6 sm:w-6 ${className}`}>
      <path d="m12 2.6 2.9 5.9 6.5.9-4.7 4.6 1.1 6.5-5.8-3-5.8 3 1.1-6.5L2.6 9.4l6.5-.9L12 2.6Z" />
    </svg>
  );
}

/* Deretan bintang. Lapisan abu digambar penuh, lalu lapisan kuning
   ditumpuk di atasnya dan dipotong selebar persentase nilainya.

   Cara ini menangani nilai pecahan - 4,1 dari 5 menghasilkan bintang
   kelima yang terisi 10%, jadi ia tampak nyaris kosong. Itu memang
   benar, bukan bug. Pembulatan ke bintang utuh akan menampilkan 4
   atau 5 bintang penuh untuk nilai 4,1, dan keduanya meleset.

   Pemotongannya menghitung jarak antar bintang sebagai bagian dari
   lebar, jadi meleset sepersekian piksel. Untuk indikator visual yang
   angkanya tertulis persis di sebelahnya, ketelitian itu memadai. */
function DeretBintang({ nilai, t }) {
  const persen = Math.max(0, Math.min(100, (nilai / 5) * 100));

  return (
    <div className="relative inline-flex" role="img" aria-label={t("rating.bintangAria", { nilai })}>
      <div className="flex w-max gap-1 text-[var(--c-faint)]">
        <Bintang /><Bintang /><Bintang /><Bintang /><Bintang />
      </div>

      <div className="absolute inset-y-0 left-0 overflow-hidden" style={{ width: `${persen}%` }}>
        <div className="flex w-max gap-1 text-amber-400">
          <Bintang /><Bintang /><Bintang /><Bintang /><Bintang />
        </div>
      </div>
    </div>
  );
}

export default function GoogleRating() {
  const { lang, t } = useLanguage();

  /* FORMAT ANGKA IKUT BAHASA, dan ini sering terlewat.

     Indonesia menulis 1.175 dan 4,1; Inggris menulis 1,175 dan 4.1 -
     titik dan koma bertukar peran. Membiarkan "id-ID" saat bahasanya
     Inggris membuat 4,1 terbaca sebagai empat koma satu oleh pembaca
     Indonesia, tapi sebagai angka yang salah format oleh pembaca
     Inggris. Untuk nilai rating, salah baca desimal bukan hal sepele.

     Locale ditulis eksplisit dari `lang`, bukan dibiarkan mengikuti
     setelan peladen - itu yang menjaga hasil render peladen dan klien
     tetap identik. */
  const locale = lang === "en" ? "en-US" : "id-ID";
  const jumlahTeks = RATING.jumlah.toLocaleString(locale);

  return (
    <div className="border-y border-[var(--c-line)] bg-[var(--c-surface)] px-4 py-12 sm:px-6 sm:py-14 md:py-16 lg:px-10">
      <div className="mx-auto max-w-[1600px]">

        {/* Menumpuk di ponsel, berjajar mulai md dengan angka di kiri dan
           tombol didorong ke kanan. Garis pemisah vertikal hanya muncul
           saat berjajar - saat menumpuk ia akan jadi garis menggantung. */}
        <div className="flex flex-col items-center gap-6 text-center md:flex-row md:items-center md:justify-between md:gap-10 md:text-left">

          <div className="flex flex-col items-center gap-4 sm:flex-row sm:gap-6 md:gap-8">

            <div className="flex items-baseline gap-2">
              {/* tabular-nums menjaga lebar angka tetap, jadi tata letak
                 tidak bergeser saat nilainya diperbarui nanti. */}
              <span className="text-4xl font-bold tabular-nums leading-none text-[var(--c-ink)] sm:text-5xl">
                {RATING.nilai.toLocaleString(locale, { minimumFractionDigits: 1 })}
              </span>
              <span className="text-lg font-semibold text-[var(--c-faint)] sm:text-xl">/ 5</span>
            </div>

            <div className="hidden h-12 w-px bg-slate-200 sm:block" />

            <div>
              <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--c-brand-mid)] sm:text-xs sm:tracking-[0.18em]">
                {t("rating.eyebrow")}
              </p>

              <div className="mb-1.5 flex justify-center sm:justify-start">
                <DeretBintang nilai={RATING.nilai} t={t} />
              </div>

              <p className="text-[13px] text-[var(--c-muted)] sm:text-sm">
                {t("rating.dari", { jumlah: jumlahTeks, tanggal: t("rating.diperiksa") })}
              </p>
            </div>
          </div>

          {/* SATU-SATUNYA ELEMEN INTERAKTIF DI BAGIAN INI, jadi efeknya
             pun hanya satu: ikon bergeser miring ke kanan atas, arah yang
             sama dengan panahnya. Itu isyarat "membuka di luar situs
             ini", dan di sini isyarat itu benar.

             Bagian ini menampilkan angka yang orang baca dan bandingkan.
             Elemen yang bergerak di sekitarnya mengganggu pembacaan. */}
          <Link
            href={RATING.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex shrink-0 items-center gap-2.5 rounded-xl bg-[var(--c-brand)] px-5 py-3 text-sm font-semibold text-white transition-colors duration-300 hover:bg-[var(--c-panel)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand)] motion-reduce:transition-none sm:px-6 sm:text-base"
          >
            {t("rating.tombol")}
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-4 w-4 transition-transform duration-300 ease-out group-hover:-translate-y-0.5 group-hover:translate-x-0.5 motion-reduce:transition-none sm:h-[18px] sm:w-[18px]">
              <path d="M7 17 17 7M9 7h8v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="sr-only">, {t("umum.tabBaru")}</span>
          </Link>

        </div>
      </div>
    </div>
  );
}
